import {
	ExclamationCircleOutlined,
	ExportOutlined,
	LogoutOutlined,
	MoonOutlined,
	SunOutlined,
} from "@ant-design/icons";
import { Divider, Image, Popconfirm, theme } from "antd";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useThemeContext } from "../contexts/context";
import { updateUser } from "../services/api-server/user";
import Emitter from "../utils/emitter";
import { switchTenant } from "../utils/switchTenant";
import { getInitials, logout } from "../utils/utils";

type ThemeMode = "light" | "dark";

// The parts of the signed-in user and of a tenant that the menu reads.
type AccountUser = {
	name?: string;
	displayName?: string;
	email?: string;
	profile_picture?: string;
} | null;

type AccountTenant = {
	value: string;
	name?: string;
	displayName?: string;
	logo?: string;
};

// Round picture, or initials when there is none.
const AccountAvatar = ({
	src,
	name,
	size = 32,
}: {
	src?: string;
	name?: string;
	size?: number;
}) => {
	return src ? (
		<Image
			className="no-visual-search"
			style={{
				border: "1px solid #d9d9d9",
				borderRadius: "180px",
				minWidth: `${size}px`,
				maxWidth: `${size}px`,
			}}
			width={size}
			height={size}
			preview={false}
			src={src}
		></Image>
	) : (
		<div
			className="profile-picture-initials"
			style={{
				border: "1px solid #d9d9d9",
				width: `${size}px`,
				minWidth: `${size}px`,
				maxWidth: `${size}px`,
				height: `${size}px`,
			}}
		>
			<span
				className="profile-initials"
				style={{ fontSize: size < 32 ? "10px" : "12px" }}
			>
				{getInitials(name, 0)}
			</span>
		</div>
	);
};

export const UserAvatar = ({
	user,
	size,
}: {
	user: AccountUser;
	size?: number;
}) => (
	<AccountAvatar
		src={user?.profile_picture}
		name={user?.name || user?.displayName} // NOSONAR: an empty stored name falls back to displayName
		size={size}
	/>
);

const tenantLabel = (tenant: AccountTenant) =>
	tenant.name || tenant.displayName; // NOSONAR: an empty stored name falls back to displayName

const openTenant = (tenant: AccountTenant) => {
	switchTenant(tenant.value, () => {
		window.location.href = "/dashboard";
	});
};

// Not onClick={logout}: the click event would be taken as autoLogout.
const signOut = () => {
	logout();
};

const openMicrosoftAccount = () => {
	window.open(
		"https://myaccount.microsoft.com/?ref=MeControl",
		"_blank",
		"noopener,noreferrer"
	);
};

/**
 * The header's account menu: the signed-in user, account actions, the
 * light/dark switch and the tenant switcher.
 */
const AccountMenu = ({
	user,
	tenants,
	currentTenantKey,
	onConfirmOpenChange,
}: {
	user: AccountUser;
	tenants: AccountTenant[] | null;
	currentTenantKey: string | null;
	onConfirmOpenChange: (open: boolean) => void;
}) => {
	const navigate = useNavigate();
	const { token } = theme.useToken();
	const { mode, setMode } = useThemeContext();
	const [isSavingMode, setSavingMode] = useState<boolean>(false);

	// Neutrals from the antd theme, so light mode gets a white surface. Also
	// set on the tenant switch confirm, which renders outside the menu.
	const menuVars = {
		"--account-menu-bg": token.colorBgElevated,
		"--account-menu-text": token.colorText,
		"--account-menu-hover": token.colorFillTertiary,
		"--account-menu-muted": token.colorTextSecondary,
		"--account-menu-shadow": token.boxShadowSecondary,
	} as React.CSSProperties;

	const nextMode: ThemeMode = mode === "light" ? "dark" : "light";

	// Saves the theme only. No "userSaved": the user reload behind it resets
	// the open page's forms. Disabled while saving, so a failed save can't
	// revert a later choice.
	const toggleMode = () => {
		const previousMode = mode;
		setSavingMode(true);
		setMode(nextMode);
		updateUser({ email: user?.email, theme: nextMode })
			.catch(() => {
				setMode(previousMode);
				Emitter.emit("alert", {
					type: "error",
					message: "Couldn't save your theme. Please try again.",
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
			})
			.finally(() => {
				setSavingMode(false);
			});
	};

	// Current tenant first. Sorted on a copy: the list is App's state.
	const sortedTenants = [...(tenants ?? [])].sort((a, b) => {
		if (a.value === currentTenantKey) {
			return -1;
		} else if (b.value === currentTenantKey) {
			return 1;
		} else {
			return 0;
		}
	});

	const renderTenant = (tenant: AccountTenant) => {
		const label = tenantLabel(tenant);
		const content = (
			<>
				<AccountAvatar src={tenant.logo} name={label} />
				<span className="account-menu-label">{label}</span>
			</>
		);

		if (tenant.value === currentTenantKey) {
			return (
				<div
					key={tenant.value}
					className="account-menu-row account-menu-tenant account-menu-tenant-current"
				>
					{content}
				</div>
			);
		}

		return (
			<Popconfirm
				key={tenant.value}
				placement="right"
				title=""
				description={`Switch to ${label}? This will log you out of the current session`}
				onOpenChange={onConfirmOpenChange}
				onCancel={() => openTenant(tenant)}
				classNames={{
					root: "popconfirm-danger popconfirm-split-buttons account-menu-confirm",
				}}
				styles={{
					root: menuVars,
					body: { maxWidth: "300px", padding: "16px" },
				}}
				okText="Stay on current tenant"
				okType="default"
				icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
				cancelText={<span>Switch</span>}
				cancelButtonProps={{ ghost: true }}
			>
				<button type="button" className="account-menu-row account-menu-tenant">
					{content}
				</button>
			</Popconfirm>
		);
	};

	return (
		<div className="account-menu" style={menuVars}>
			<button
				type="button"
				className="account-menu-row account-menu-user"
				onClick={() => navigate("/profile")}
			>
				<UserAvatar user={user} size={36} />
				<span className="account-menu-user-text">
					<span className="account-menu-label account-menu-user-name">
						{user?.name}
					</span>
					<span className="account-menu-label account-menu-user-email">
						{user?.email}
					</span>
				</span>
			</button>
			<button
				type="button"
				className="account-menu-row"
				onClick={openMicrosoftAccount}
			>
				<ExportOutlined />
				<span>View Microsoft 365 account</span>
			</button>
			<button
				type="button"
				className="account-menu-row"
				disabled={!user?.email || isSavingMode}
				onClick={toggleMode}
			>
				{nextMode === "dark" ? <MoonOutlined /> : <SunOutlined />}
				<span>{nextMode === "dark" ? "Dark mode" : "Light mode"}</span>
			</button>
			<button
				type="button"
				className="account-menu-row account-menu-sign-out"
				onClick={signOut}
			>
				<LogoutOutlined />
				<span>Sign out</span>
			</button>
			{sortedTenants.length > 0 && (
				<>
					<Divider className="account-menu-divider" orientation="left">
						Switch tenant
					</Divider>
					<div className="account-menu-tenants">
						{sortedTenants.map(renderTenant)}
					</div>
				</>
			)}
		</div>
	);
};

export default AccountMenu;
